'use client';

import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { css, cx } from 'styled-system/css';

import AppIcon from '@shaojiang/components/AppIcon';
import ShaojiangLink from '@shaojiang/components/ShaojiangLink';
import Tooltip from '@shaojiang/components/Tooltip';
import { Collapsible } from '@shaojiang/components/ui/collapsible';
import useLocale from '@shaojiang/hooks/useLocale';
import { useDomStore } from '@shaojiang/store/dom';
import { IMenu } from '@shaojiang/types/common';

interface Props {
  menu: IMenu;
  showMini: boolean;
}

/** Pill-shaped item, gradient fill when selected (same look as the old MUI ListItemButton). */
const itemBase = css({
  display: 'flex',
  alignItems: 'center',
  gap: '3',
  width: 'full',
  px: '4',
  py: '2.5',
  my: '1',
  borderRightRadius: 'full',
  color: 'fg.default',
  fontWeight: 'medium',
  cursor: 'pointer',
  textDecoration: 'none',
  transition: 'background 0.15s ease',
  _hover: { textDecoration: 'none', bgColor: 'gray.a3' },
});

const itemSelected = css({
  color: 'white',
  boxShadow: 'raised',
  bgGradient: 'to-r',
  gradientFrom: '#c6a7fe',
  gradientTo: 'primary.9',
});

const itemCompact = css({ py: '1.5' });
const subItemIndented = css({ pl: '8' });
const subItemMini = css({ pl: '4' });

const itemStyle = (selected: boolean, compact = false) =>
  cx(itemBase, compact && itemCompact, selected && itemSelected);

export default function Menu({ menu, showMini }: Props) {
  const setMobileSidebarOpen = useDomStore((state) => state.setMobileSidebarOpen);
  const { pathName } = useLocale();
  const selected = menu.href === '/' ? pathName === '/' : pathName.startsWith(menu.href);
  const hasSubMenus = Number(menu.subMenus?.length) > 0;
  const [open, setOpen] = React.useState(hasSubMenus && selected);

  const hideMobileSidebar = () => setMobileSidebarOpen(false);

  const label = (
    <>
      <Tooltip title={showMini ? menu.title : ''} placement='right'>
        <span className={css({ display: 'inline-flex' })}>
          <AppIcon icon={menu.icon} size='lg' contrast={selected} />
        </span>
      </Tooltip>
      {!showMini && <span className={css({ flex: '1', textAlign: 'start' })}>{menu.title}</span>}
    </>
  );

  if (!hasSubMenus) {
    return (
      <li>
        <ShaojiangLink
          href={menu.href}
          onClick={hideMobileSidebar}
          className={itemStyle(selected)}
          aria-current={selected ? 'page' : undefined}
        >
          {label}
        </ShaojiangLink>
      </li>
    );
  }

  return (
    <li>
      <Collapsible.Root open={open} onOpenChange={({ open }) => setOpen(open)}>
        <Collapsible.Trigger className={itemStyle(selected)}>
          {label}
          {!showMini && (open ? <ChevronUp size={18} /> : <ChevronDown size={18} />)}
        </Collapsible.Trigger>
        <Collapsible.Content>
          <ul>
            {(menu.subMenus || []).map((subMenu) => {
              const href = `${menu.href}${subMenu.href}`;
              const exactSelect = href === pathName;
              return (
                <li key={subMenu.href}>
                  <ShaojiangLink
                    href={href}
                    onClick={hideMobileSidebar}
                    className={cx(
                      itemStyle(exactSelect, true),
                      showMini ? subItemMini : subItemIndented,
                    )}
                    aria-current={exactSelect ? 'page' : undefined}
                  >
                    <Tooltip title={showMini ? subMenu.title : ''} placement='right'>
                      <span className={css({ display: 'inline-flex' })}>
                        <AppIcon icon={subMenu.icon} size='md' contrast={exactSelect} />
                      </span>
                    </Tooltip>
                    {!showMini && <span>{subMenu.title}</span>}
                  </ShaojiangLink>
                </li>
              );
            })}
          </ul>
        </Collapsible.Content>
      </Collapsible.Root>
    </li>
  );
}
